// https://flowbite.com/docs/components/modal/#typescript

import { Component, h, Prop, Event, EventEmitter } from '@stencil/core';
import { Modal } from 'flowbite';
import state from '../../components/store/globalStore';
import Icon from '../../ui-components/Icon/Icon';

export interface CloseModalClickEvent {
    modalId: string;
}

@Component({
    tag: 'base-modal',
    styleUrl: 'BaseModal.scss',
    shadow: true
})
export class BaseModal {
    @Prop() modalId: string;
    @Prop() brand: string;
    @Prop() modalOptions?: any;
    @Prop() modalHeader?: string | HTMLElement | boolean;
    @Prop() modalBody: string | HTMLElement;
    @Prop() modalFooter?: string | HTMLElement | boolean;

    targetEl: HTMLDivElement;
    modal: Modal;

    async componentDidLoad() {
        const modifiedModalOptions = {
            ...this.modalOptions,
            placement: 'top-center',
            backdrop: 'dynamic',
            backdropClasses: 'fade modal-backdrop in',
            closable: false,
        };
        const instanceOptions = {
            id: this.modalId,
            override: true
        }

        const modalLoaded = await customElements.whenDefined('base-modal');
        if (modalLoaded) {
            this.modal = new Modal(this.targetEl, modifiedModalOptions, instanceOptions);

            // hack needed to fix flowbite bug
            this.modal._destroyBackdropEl = () => {
                if (!this.modal.isHidden()) {
                    document.querySelector('.modal-backdrop')?.remove();
                }
            }

            state.showModal = (instanceId: any) => {
                this.setModalInitialized(instanceId, true);
                const instance = (window as any)?.FlowbiteInstances?.getInstance('Modal', instanceId) || (this.modalId === instanceId && this.modal);
                instance?.show();
            }
            state.hideModal = (instanceId: any) => {
                state.isADILoading = true;
                this.setModalInitialized(instanceId, false);
                const instance = (window as any)?.FlowbiteInstances?.getInstance('Modal', instanceId) || (this.modalId === instanceId && this.modal);
                instance?.hide();
            }
        }
    }

    @Event({ eventName: 'lactCloseModalClick', composed: true })
    lactCloseModalClick: EventEmitter<CloseModalClickEvent>;
    closeModal = () => {
        state.hideModal(this.modalId);
        this.lactCloseModalClick.emit({ modalId: this.modalId });
    }

    setModalInitialized(instanceId: string, val: boolean) {
        if (state.modalInitialized[instanceId] !== val) {
            state.modalInitialized = { ...state.modalInitialized, [instanceId]: val };
        }
    }

    closeButton() {
        return (
            <button type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-0 align-middle ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white" data-modal-hide="static-modal" onClick={this.closeModal}>
                {Icon.CloseIcon('', '24', '24')}
                <span class="sr-only">Close modal</span>
            </button>
        );
    };

    render = () => {
        return (
          <tailwind-css>
            <host class={`theme-${this.brand} font-skin`}>
              <div
                id={this.modalId}
                data-modal-target="medium-modal"
                data-modal-backdrop="static"
                ref={el => (this.targetEl = el as HTMLDivElement)}
                tabindex="-1"
                aria-hidden="true"
                class="modal-z-index hidden overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 justify-center items-center md:inset-0 h-[calc(100%-1rem)] max-h-full w-full self-center"
              >
                {state.modalInitialized[this.modalId] && (
                  <div class="relative md:!w-auto md:p-0 p-4 w-full max-h-full">
                    <div class="relative bg-white c dark:bg-gray-700 rounded shadow-md">
                      {this.modalHeader ? (
                        <div class="flex items-center justify-between p-4 md:p-4 border-b rounded-t dark:border-gray-600">
                          <h3 class="base-modal-header text-xl font-semibold text-gray-900 dark:text-white w-full">{this.modalHeader}</h3>
                          {this.closeButton()}
                        </div>
                      ) : (
                        <div class="flex justify-between items-center p-6 -mb-3 pb-0">
                          <div class="flex space-x-4">
                              <div>{Icon.ATCLogo('30', '98')}</div>
                              <div class="flex flex-col px-6"><hr class="flex-1 border-l-[#e1e4e8] border-t-[#e1e4e8] border-t border-solid border-l"/></div>
                              <div>{Icon.KBBLogo('36', '122')}</div>
                          </div>
                          <div>{this.closeButton()}</div>
                        </div>
                      )}

                      <div class={`p-4 md:p-6 base-modal-body ${state.isUserFraudulent ? '' : 'space-y-4'}`}>{this.modalBody}</div>

                      {this.modalFooter && <div class="p-4 md:p-4 border-t border-gray-200 rounded-b dark:border-gray-600">{this.modalFooter}</div>}

                      {/* Add supported tailwind styles */}
                      <div class="hidden">
                        <div class="flex-row gap-4 space-y-4 py-3 py-4 pl-4 mb-0 dark:border-gray-600 text-blue-800 justify-end max-w-screen-md"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </host>
          </tailwind-css>
        );
    }
}
